import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import {
  gql,
  GraphQLAPI,
  GraphQLResult,
  parseItem,
  queries,
} from "@examspark/api";
import {
  Maybe,
  ModelResourceConnection,
  SearchableResourceConnectionNew,
  Unit,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

export const useQueryGetUnitItems = (
  workspaceId: string | undefined,
  unit?: Maybe<Unit>,
) => {
  const subject = unit?.subject?.[0];
  const unitLabel = unit?.numberLabel?.padStart(2, "0");
  console.log("unit", unit);
  return useQuery({
    queryKey: ["items", workspaceId, subject, unitLabel],
    enabled: !!workspaceId && !!unit && !!unit?.id,
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.unit.listUnitItems,
        variables: {
          input: {
            workspaceId,
            id: unit?.id,
          },
        },
      })) as GraphQLResult<{ listUnitItems: ModelResourceConnection }>;

      return (
        result.data?.listUnitItems.items?.filter(
          (item) =>
            !item.resource?.isDeleted &&
            item.resource?.maturity === "OPERATIONAL",
        ) ?? []
      );
    },
  });
};

export const useQueryGetItem = (itemId?: string, itemSk?: string) => {
  return useQuery({
    queryKey: ["item", itemId, itemSk],
    enabled: !!itemId && !!itemSk,
    queryFn: async () => {
      const result = (
        (await GraphQLAPI.query({
          query: queries.item.getItemForPreview,
          variables: {
            id: itemId,
            sk: itemSk,
          },
        })) as any
      ).data.getResource;

      return parseItem(result);
    },
  });
};

export const useQuerySearchClonedItems = (params: {
  targetBankId: string;
  originResourceId: string | undefined;
  limit?: number;
}) => {
  const authorizationContext = useAuthorizationContext();

  return useInfiniteQuery({
    queryKey: ["cloned-items", params.originResourceId],
    enabled: !!params.originResourceId,
    queryFn: async ({ pageParam = "" }) => {
      if (!params.originResourceId) return null;

      // if the ID we are passed is already a clone, we want to find siblings
      const itemData = await gql.operations.item.getItemForClone(
        params.originResourceId.split("/")[0],
        authorizationContext,
      );

      return (
        (await GraphQLAPI.query({
          query: queries.item.searchItems,
          variables: {
            limit: params.limit ?? 25,
            nextToken: pageParam,
            filter: {
              and: [
                {
                  bankId: {
                    eq: params.targetBankId,
                  },
                },
                {
                  or: [
                    {
                      originResourceId: {
                        eq: params.originResourceId,
                      },
                    },
                    {
                      originResourceId: {
                        eq: itemData?.originResourceId ?? "__empty__",
                      },
                    },
                  ],
                },
              ],
            },
            authorizationContext,
          },
        })) as GraphQLResult<{
          searchUserResources: SearchableResourceConnectionNew;
        }>
      ).data?.searchUserResources;
    },

    getNextPageParam: (lastPage) => {
      return lastPage?.nextToken ?? undefined;
    },
  });
};
